import React, { useLayoutEffect, useRef } from 'react';
import { captionForVoiceState } from '../../lib/voiceTurnController';
import VoiceOrb from './VoiceOrb';
import './LiveVoicePanel.css';

function MicIcon({ muted = false }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {muted ? (
        <>
          <path d="M4 4l16 16" />
          <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
          <path d="M15 9V7a3 3 0 0 0-5.77-1.2" />
          <path d="M17 11a5 5 0 0 1-8.46 3.61" />
          <path d="M5 11a7 7 0 0 0 11.08 5.73" />
          <path d="M12 19v3M8 22h8" />
        </>
      ) : (
        <>
          <rect x="9" y="3" width="6" height="11" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 22h8" />
        </>
      )}
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export default function LiveVoicePanel({
  liveVoiceState,
  isMuted,
  ccEnabled,
  liveTranscript = '',
  assistantPreview = '',
  onToggleMute,
  onToggleCC,
  onInterrupt,
  onClose,
  onThinkingVisible,
  micLevel = null,
  speechActive = false,
}) {
  const isThinking = liveVoiceState === 'transcribing' || liveVoiceState === 'sending' || liveVoiceState === 'thinking';
  const isResponding = liveVoiceState === 'speaking' || liveVoiceState === 'interruption_check';
  const isError = liveVoiceState === 'error';
  const isMutedState = isMuted || liveVoiceState === 'idle' || liveVoiceState === 'muted';
  const preservesOutputState = isThinking || isResponding;
  const normalizedVisualState = liveVoiceState === 'thinking' ? 'sending' : liveVoiceState;
  // Interruption validation is part of response playback, not a new visual
  // mode. It must use the exact same orb and interrupt target as SPEAKING.
  const presentationState = normalizedVisualState === 'interruption_check'
    ? 'speaking'
    : normalizedVisualState;
  const visualState = isMuted && !preservesOutputState
    ? 'muted'
    : presentationState;

  const accessibleState = isError
    ? 'Voice connection interrupted'
    : isThinking
      ? `Generating response${isMuted ? '; microphone muted' : ''}`
      : isResponding
        ? `Response playback${isMuted ? '; microphone muted' : ''}`
        : isMutedState
          ? 'Microphone muted'
          : 'Voice input active';
  const thinkingVisibleReportedRef = useRef(false);
  useLayoutEffect(() => {
    if (!isThinking) {
      thinkingVisibleReportedRef.current = false;
      return undefined;
    }
    if (thinkingVisibleReportedRef.current) return undefined;
    const report = () => {
      if (thinkingVisibleReportedRef.current) return;
      thinkingVisibleReportedRef.current = true;
      onThinkingVisible?.();
    };
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      // The first callback runs before its frame is painted; report from the
      // following frame so the metric represents a completed browser paint
      // opportunity rather than the React state assignment itself.
      let firstFrame = null;
      let secondFrame = null;
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(report);
      });
      return () => {
        if (firstFrame !== null) window.cancelAnimationFrame?.(firstFrame);
        if (secondFrame !== null) window.cancelAnimationFrame?.(secondFrame);
      };
    }
    return undefined;
  }, [isThinking, onThinkingVisible]);

  // Keep one central caption and hand it to the current speaker immediately.
  // THINKING uses the same orb plus one static, content-free cue so processing
  // is visible without inventing progress or synthetic status stages.
  const captionText = captionForVoiceState({
    state: liveVoiceState,
    userTranscript: liveTranscript,
    assistantText: assistantPreview,
  });
  const visibleCaption = captionText || (
    isResponding
      ? 'Speaking…'
      : isMutedState
        ? 'Microphone muted'
        : ''
  );

  return (
    <div
      className="br-voice-surface"
      data-testid="live-voice-surface"
      data-voice-state={visualState}
      data-voice-lifecycle-state={liveVoiceState}
      data-mic-muted={isMuted ? 'true' : 'false'}
      style={{ backgroundImage: "url('/background-stars.png')" }}
    >
      <div className="br-voice-layout">
        <header className="br-voice-header">
          <div className="br-voice-session-mark" aria-hidden="true">
            <span />
            Voice session
          </div>

          <div className="br-voice-header-controls">
            <button type="button" onClick={onToggleCC} aria-pressed={ccEnabled} className="br-voice-control br-voice-cc" data-testid="live-cc-toggle">
              CC
            </button>
            <button type="button" onClick={onClose} className="br-voice-control br-voice-close" aria-label="Close live voice" data-testid="live-voice-close">
              <CloseIcon />
            </button>
          </div>
        </header>

        <main className="br-voice-stage">
          <div className="br-voice-orb-wrap">
            <div className="br-voice-orb-presence" aria-hidden="true">
              <span className="br-voice-orb-radiance br-voice-orb-radiance--field" />
              <span className="br-voice-orb-radiance br-voice-orb-radiance--caustic" />
              <span className="br-voice-orb-halo br-voice-orb-halo--outer" />
              <span className="br-voice-orb-depth" />
              <span className="br-voice-orb-halo br-voice-orb-halo--middle" />
              <span className="br-voice-orb-halo br-voice-orb-halo--inner" />
              <span className="br-voice-orb-signal br-voice-orb-signal--one" />
              <span className="br-voice-orb-signal br-voice-orb-signal--two" />
            </div>
            <span className="br-voice-orb-center">
              <span className="br-voice-orb-core-fallback" data-testid="live-voice-orb-core" aria-hidden="true" />
              <VoiceOrb
                state={visualState}
                inputEnergy={micLevel}
                micMuted={isMuted}
                speechActive={speechActive}
              />
            </span>
            <div className="br-voice-thinking-aura" data-testid="live-voice-thinking-aura" aria-hidden="true" />
            {isResponding && (
              <button type="button" className="br-voice-interrupt" aria-label="Interrupt Sonny" title="Interrupt Sonny" onClick={onInterrupt} data-testid="live-voice-interrupt">
                <span className="sr-only">Interrupt Sonny</span>
              </button>
            )}
          </div>

          <div className="br-voice-caption-zone" aria-live="polite">
            <span className="sr-only" data-testid="live-voice-state-announcement">{accessibleState}</span>
            {ccEnabled && visibleCaption && (
              <p className="br-voice-caption" aria-label="Live conversation transcript" data-testid="live-voice-transcript">
                {visibleCaption}
              </p>
            )}
          </div>
        </main>

        <footer className="br-voice-footer">
          <button
            type="button"
            onClick={onToggleMute}
            className={`br-voice-mic${isMuted ? ' is-muted' : ''}`}
            aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
            aria-pressed={isMuted}
            data-muted={isMuted ? 'true' : 'false'}
            data-testid="live-voice-mute"
          >
            <MicIcon muted={isMuted} />
          </button>
          <div aria-hidden="true" />
          <div className="br-voice-footer-balance" aria-hidden="true" />
        </footer>
      </div>
    </div>
  );
}
